'use client';

import type { DragEvent, ReactNode, RefObject } from 'react';
import { useTranslations } from 'next-intl';
import { HStack, VStack } from '@terra/ui-styled-system';
import {
  IconButton,
  Input,
  LabelDefault,
  Menu,
  MetaDefault,
  MonoSmall,
  Tooltip,
} from '@terra/ui-design-system';
import {
  Tick01Icon,
  ArrowDown01Icon,
  ArrowRight01Icon,
  MoreHorizontalIcon,
  Edit03Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { DEFAULT_REPLAY_COLLECTION_ID } from '../../store/replaySessionStore';
import type { ReplayCollection } from '../../types/replay';
import { REPLAY_COLLECTION_DRAG_TYPE } from '../../store/replay-session-order';

type ReplayCollectionGroupProps = {
  collection: ReplayCollection;
  sessionCount: number;
  isCollapsed: boolean;
  isActive: boolean;
  isHovered: boolean;
  isRenaming: boolean;
  isDragOver: boolean;
  draggedSessionId: string | null;
  isRenameDuplicate: boolean;
  renamingCollectionName: string;
  renameInputRef: RefObject<HTMLInputElement | null>;
  children: ReactNode;
  onHoverChange: (collectionId: string | null) => void;
  onDragOver: () => void;
  onDragLeave: () => void;
  onDragEnd: () => void;
  onDrop: (event: { sessionId: string; collectionId: string }) => void;
  onReorderCollections: (event: {
    collectionId: string;
    beforeCollectionId: string;
  }) => void;
  onSelect: () => void;
  onToggleCollapsed: () => void;
  onRenamingNameChange: (name: string) => void;
  onSubmitRename: () => void;
  onCancelRename: () => void;
  onStartRename: () => void;
  onRequestDelete: () => void;
};

export function ReplayCollectionGroup({
  collection,
  sessionCount,
  isCollapsed,
  isActive,
  isHovered,
  isRenaming,
  isDragOver,
  draggedSessionId,
  isRenameDuplicate,
  renamingCollectionName,
  renameInputRef,
  children,
  onHoverChange,
  onDragOver,
  onDragLeave,
  onDragEnd,
  onDrop,
  onReorderCollections,
  onSelect,
  onToggleCollapsed,
  onRenamingNameChange,
  onSubmitRename,
  onCancelRename,
  onStartRename,
  onRequestDelete,
}: ReplayCollectionGroupProps) {
  const tCollections = useTranslations('replay.collections');

  const handleMouseEnter = () => {
    onHoverChange(collection.id);
  };

  const handleMouseLeave = () => {
    onHoverChange(null);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    onDragOver();
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const draggedCollectionId = event.dataTransfer.getData(
      REPLAY_COLLECTION_DRAG_TYPE,
    );

    if (draggedCollectionId) {
      onReorderCollections({
        collectionId: draggedCollectionId,
        beforeCollectionId: collection.id,
      });
      onDragLeave();

      return;
    }

    const sessionId =
      event.dataTransfer.getData('text/plain') || draggedSessionId;

    if (sessionId) {
      onDrop({ sessionId, collectionId: collection.id });
    }

    onDragLeave();
  };

  const handleDragStart = (event: DragEvent<HTMLDivElement>) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData(REPLAY_COLLECTION_DRAG_TYPE, collection.id);
  };

  return (
    <VStack
      gap="0"
      alignItems="stretch"
      w="full"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onDragOver={handleDragOver}
      onDragLeave={onDragLeave}
      onDrop={handleDrop}
    >
      <HStack
        gap="1"
        px="2"
        py="1"
        w="full"
        flexShrink={0}
        bg={isDragOver ? 'bg.muted' : 'transparent'}
        cursor="pointer"
        borderWidth={isDragOver ? '1px' : '0'}
        borderColor="colorPalette.emphasized"
        draggable={!isRenaming}
        _hover={{ bg: 'bg.muted' }}
        onClick={onSelect}
        onDragStart={handleDragStart}
        onDragEnd={onDragEnd}
      >
        <IconButton
          variant="link"
          size="xs"
          aria-label={
            isCollapsed
              ? tCollections('expandCollection')
              : tCollections('collapseCollection')
          }
          minW="auto"
          h="auto"
          p="0.5"
          color="fg.muted"
          onClick={(event) => {
            event.stopPropagation();
            onToggleCollapsed();
          }}
        >
          {isCollapsed ? (
            <ArrowRight01Icon size={12} />
          ) : (
            <ArrowDown01Icon size={12} />
          )}
        </IconButton>
        {isRenaming ? (
          <>
            <Tooltip
              title={isRenameDuplicate ? tCollections('nameAlreadyTaken') : ''}
              disabled={!isRenameDuplicate}
              positioning={{ placement: 'bottom', strategy: 'fixed' }}
            >
              <Input
                ref={renameInputRef}
                size="xs"
                value={renamingCollectionName}
                onChange={(event) => onRenamingNameChange(event.target.value)}
                onClick={(event) => event.stopPropagation()}
                onBlur={() => {
                  if (isRenameDuplicate) {
                    onCancelRename();

                    return;
                  }

                  onSubmitRename();
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    onSubmitRename();
                  }

                  if (event.key === 'Escape') {
                    onCancelRename();
                  }
                }}
                borderColor={isRenameDuplicate ? 'border.error' : undefined}
                aria-invalid={isRenameDuplicate || undefined}
                flex="1"
                minW="0"
                h="22px"
              />
            </Tooltip>
            <IconButton
              variant="link"
              size="xs"
              aria-label={tCollections('saveCollectionName')}
              minW="auto"
              h="auto"
              p="1"
              disabled={isRenameDuplicate}
              onClick={onSubmitRename}
            >
              <Tick01Icon size={10} />
            </IconButton>
          </>
        ) : (
          <>
            <LabelDefault
              fontWeight={isActive ? 'medium' : 'normal'}
              color={isActive ? 'fg.default' : 'fg.muted'}
              truncate
              flex="1"
              minW="0"
              onDoubleClick={(event) => {
                event.stopPropagation();
                onStartRename();
              }}
            >
              {collection.name}
            </LabelDefault>
            <MonoSmall color="fg.muted">{sessionCount}</MonoSmall>
            {isDragOver ? (
              <MetaDefault color="fg.muted">
                {tCollections('dropHere')}
              </MetaDefault>
            ) : null}
          </>
        )}
        <Menu.Root positioning={{ strategy: 'fixed' }}>
          <Menu.Trigger asChild>
            <IconButton
              variant="link"
              size="xs"
              aria-label={tCollections('collectionMenu')}
              minW="auto"
              h="auto"
              p="1"
              opacity={isHovered || isActive ? 1 : 0}
              visibility={isHovered || isActive ? 'visible' : 'hidden'}
              onClick={(event) => event.stopPropagation()}
            >
              <MoreHorizontalIcon size={10} />
            </IconButton>
          </Menu.Trigger>
          <Menu.Positioner>
            <Menu.Content minW="140px" py="1">
              <Menu.Item value="rename" onClick={onStartRename} py="1" px="2">
                <HStack gap="1.5">
                  <Edit03Icon size={12} />
                  <LabelDefault>
                    {tCollections('renameCollection')}
                  </LabelDefault>
                </HStack>
              </Menu.Item>
              {collection.id !== DEFAULT_REPLAY_COLLECTION_ID ? (
                <Menu.Item
                  value="delete"
                  onClick={onRequestDelete}
                  py="1"
                  px="2"
                >
                  <HStack gap="1.5">
                    <Cancel01Icon size={12} />
                    <LabelDefault>
                      {tCollections('deleteCollection')}
                    </LabelDefault>
                  </HStack>
                </Menu.Item>
              ) : null}
            </Menu.Content>
          </Menu.Positioner>
        </Menu.Root>
      </HStack>
      {!isCollapsed ? (
        <VStack gap="0" alignItems="stretch" w="full">
          {children}
        </VStack>
      ) : null}
    </VStack>
  );
}
